import { useEffect, useRef, useState } from "react";
import MonacoEditor, { loader, Monaco } from "@monaco-editor/react";
import { AlertCircle, HelpCircle } from "lucide-react";
import { Alert, AlertDescription } from "~/components/ui/alert";
import { Button } from "~/components/ui/button";
import { Checkbox } from "~/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import { BaseInput } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import { Tooltip, TooltipContent, TooltipTrigger } from "~/components/ui/tooltip";
import * as monaco from "~/utils/monaco-editor";

loader.config({ monaco });

export type SkillTag = "ORCHESTRATION" | "ANALYSIS";

export interface SkillFormData {
  name: string;
  description: string;
  tags: SkillTag[];
  content: string;
}

export interface SkillFormErrors {
  name?: string;
  description?: string;
  tags?: string;
  content?: string;
  general?: string;
}

interface SkillEditorModalProps {
  errors?: SkillFormErrors;
  loading?: boolean;
  onClose: () => void;
  onSubmit: (data: SkillFormData) => void | Promise<void>;
  open: boolean;
  initialData?: Partial<SkillFormData>;
  isEditing?: boolean;
}

const AVAILABLE_TAGS: { value: SkillTag; label: string; description: string }[] = [
  {
    value: "ORCHESTRATION",
    label: "Orchestration",
    description: "Skills that help coordinate and manage workflows",
  },
  {
    value: "ANALYSIS",
    label: "Analysis",
    description: "Skills that help analyze and interpret data",
  },
];

const DEFAULT_CONTENT = `When using this skill, always:

1. **First step**: Describe what to do first
2. **Second step**: Describe what to do next
3. **Third step**: Additional instructions

Keep responses clear and actionable.
`;

const SkillEditorModal = ({
  errors = {},
  loading = false,
  onClose,
  onSubmit,
  open,
  initialData,
  isEditing = false,
}: SkillEditorModalProps) => {
  const monacoRef = useRef<Monaco | null>(null);

  const [name, setName] = useState(initialData?.name ?? "");
  const [description, setDescription] = useState(initialData?.description ?? "");
  const [tags, setTags] = useState<SkillTag[]>(initialData?.tags ?? []);
  const [content, setContent] = useState(initialData?.content ?? DEFAULT_CONTENT);

  // Sync internal state when initialData prop changes (e.g., after async fetch)
  useEffect(() => {
    setName(initialData?.name ?? "");
    setDescription(initialData?.description ?? "");
    setTags(initialData?.tags ?? []);
    setContent(initialData?.content ?? DEFAULT_CONTENT);
  }, [initialData]);

  const handleTagToggle = (tag: SkillTag) => {
    setTags((prev) => (prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag]));
  };

  const handleSubmit = () => {
    onSubmit({ name, description, tags, content });
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="sm:max-w-[900px] sm:w-[calc(100%_-_64px)] p-0 gap-0 max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader className="px-6 pt-6 pb-2 shrink-0">
          <DialogTitle>{isEditing ? "Edit Skill" : "Add New Skill"}</DialogTitle>
        </DialogHeader>

        <div className="px-6 pt-4 pb-4 overflow-y-auto flex-1">
          {errors.general && (
            <Alert variant="destructive" className="mb-4">
              <AlertCircle />
              <AlertDescription>{errors.general}</AlertDescription>
            </Alert>
          )}

          <div className="space-y-4">
            {/* Name and Tags row */}
            <div className="flex gap-4 items-start">
              <div className="flex-1">
                <BaseInput
                  placeholder="my-skill-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  disabled={loading}
                  errorMessage={errors.name}
                  aria-invalid={!!errors.name}
                  topElement={
                    <div className="flex items-center gap-1">
                      <Label>Name</Label>
                      <Tooltip delayDuration={200}>
                        <TooltipTrigger asChild>
                          <HelpCircle className="h-3.5 w-3.5 text-muted-foreground cursor-help" />
                        </TooltipTrigger>
                        <TooltipContent side="right" className="max-w-[220px] z-[1300]">
                          Use lowercase letters, numbers, and hyphens. This is how you&apos;ll
                          invoke the skill (e.g., /my-skill-name).
                        </TooltipContent>
                      </Tooltip>
                    </div>
                  }
                />
              </div>
              <div className="flex flex-col gap-2">
                <Label>Tags</Label>
                <div className="flex gap-4 h-9 items-center">
                  {AVAILABLE_TAGS.map((tag) => (
                    <Tooltip key={tag.value} delayDuration={200}>
                      <TooltipTrigger asChild>
                        <div className="flex items-center gap-1.5 cursor-help">
                          <Checkbox
                            id={`tag-${tag.value}`}
                            checked={tags.includes(tag.value)}
                            onCheckedChange={() => handleTagToggle(tag.value)}
                            disabled={loading}
                          />
                          <label
                            htmlFor={`tag-${tag.value}`}
                            className="text-sm cursor-pointer select-none"
                          >
                            {tag.label}
                          </label>
                        </div>
                      </TooltipTrigger>
                      <TooltipContent side="bottom" className="z-[1300]">
                        {tag.description}
                      </TooltipContent>
                    </Tooltip>
                  ))}
                </div>
                {errors.tags && <span className="text-xs text-destructive">{errors.tags}</span>}
              </div>
            </div>

            {/* Description field */}
            <div className="flex flex-col gap-2">
              <Label htmlFor="skill-description">Description</Label>
              <BaseInput
                id="skill-description"
                placeholder="What this skill does and when to use it..."
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                disabled={loading}
                errorMessage={errors.description}
                aria-invalid={!!errors.description}
              />
            </div>

            {/* Content field */}
            <div className="flex flex-col gap-2">
              <Label htmlFor="skill-content">Instructions</Label>
              <div className="border border-gray-200 rounded-md overflow-hidden">
                <MonacoEditor
                  height="300px"
                  language="markdown"
                  value={content}
                  beforeMount={(m) => {
                    monacoRef.current = m;
                  }}
                  onChange={(v) => setContent(v ?? "")}
                  options={{
                    automaticLayout: true,
                    fixedOverflowWidgets: true,
                    formatOnPaste: true,
                    formatOnType: true,
                    minimap: { enabled: false },
                    readOnly: loading,
                    scrollBeyondLastLine: false,
                    wordBasedSuggestions: "off",
                    wordWrap: "on",
                  }}
                />
              </div>
              {errors.content && <span className="text-xs text-destructive">{errors.content}</span>}
            </div>
          </div>
        </div>

        <DialogFooter className="px-6 py-4 shrink-0 border-t">
          <Button variant="outline" onClick={onClose} disabled={loading} size="sm">
            Cancel
          </Button>
          <Button onClick={handleSubmit} loading={loading} size="sm" className="min-w-[72px]">
            {isEditing ? "Save" : "Add"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default SkillEditorModal;
